// src/v2/hooks/useReportHousingLoan.js
import { useState, useCallback } from 'react';
import reportsService from '../services/reportsService';
import { useGeneralMessage } from '../../common/APICommon';
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

export const useReportHousingLoan = () => {
  const [loading, setLoading] = useState(false);
  const { showGeneralMessage, showMessage, message, isSuccess } = useGeneralMessage();

  /**
   * Generates the Pag-IBIG Housing Loan Remittance List from scratch
   */
  const downloadPDF = useCallback(async (data, month, year, companyName) => {
    try {
      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([612, 792]); // Letter Size
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      let currentY = 742;

      // --- HEADER ---
      page.drawText("PAG-IBIG HOUSING LOAN REMITTANCE LIST", { x: 50, y: currentY, size: 14, font: fontBold });
      currentY -= 20;
      page.drawText(`For the Period: ${month} ${year}`, { x: 50, y: currentY, size: 10, font });
      currentY -= 15;
      page.drawText(`Employer Name: ${companyName}`, { x: 50, y: currentY, size: 10, font });
      currentY -= 30;

      // --- TABLE HEADERS ---
      page.drawRectangle({ x: 50, y: currentY - 20, width: 512, height: 20, color: rgb(0.9, 0.9, 0.9) });
      const cols = { mid: 60, name: 160, hlan: 320, amount: 480 };

      page.drawText("Pag-IBIG MID No.", { x: cols.mid, y: currentY - 14, size: 8, font: fontBold });
      page.drawText("Employee Name", { x: cols.name, y: currentY - 14, size: 8, font: fontBold });
      page.drawText("Housing Loan No. (HLAN)", { x: cols.hlan, y: currentY - 14, size: 8, font: fontBold });
      page.drawText("Amount", { x: cols.amount, y: currentY - 14, size: 8, font: fontBold });

      currentY -= 20;

      // --- DATA ROWS ---
      if (!data || data.length === 0) {
        currentY -= 30;
        page.drawText("NO HOUSING LOAN DEDUCTIONS FOUND FOR THIS PERIOD", { x: 170, y: currentY, size: 9, font });
      } else {
        data.forEach((row, index) => {
          currentY -= 20;
          // Zebra striping
          if (index % 2 === 1) page.drawRectangle({ x: 50, y: currentY, width: 512, height: 20, color: rgb(0.98, 0.98, 0.98) });

          page.drawText(row.pagibigNumber || "-", { x: cols.mid, y: currentY + 6, size: 8, font });
          page.drawText(row.fullName || "-", { x: cols.name, y: currentY + 6, size: 8, font });
          page.drawText(row.hlan || "-", { x: cols.hlan, y: currentY + 6, size: 8, font });
          page.drawText((row.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 }), { x: cols.amount, y: currentY + 6, size: 8, font });

          // Row line
          page.drawLine({
            start: { x: 50, y: currentY },
            end: { x: 562, y: currentY },
            thickness: 0.2,
            color: rgb(0.8, 0.8, 0.8),
          });
        });

        // TOTAL FOOTER
        currentY -= 30;
        const total = data.reduce((sum, item) => sum + (item.amount || 0), 0);
        page.drawText("TOTAL REMITTANCE:", { x: 320, y: currentY, size: 10, font: fontBold });
        page.drawText(total.toLocaleString('en-US', { minimumFractionDigits: 2 }), { x: 480, y: currentY, size: 10, font: fontBold });
      }

      // --- DOWNLOAD TRIGGER ---
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `PagIBIG_Housing_Remittance_${month}_${year}.pdf`;
      link.click();

      showGeneralMessage("Housing Loan report downloaded.", true);
    } catch (err) {
      console.error(err);
      showGeneralMessage("Failed to generate PDF.", false);
    }
  }, [showGeneralMessage]);

  const generateAndDownload = useCallback(async (params, month, year, companyName) => {
    setLoading(true);
    try {
      let data = [];
      try {
        data = await reportsService.getReportData('HousingLoan', params);
      } catch (apiErr) {
        console.warn("Backend not found. Simulating successful empty report.");
        data = []; // Demo mode
      }
      await downloadPDF(data || [], month, year, companyName);
    } catch (err) {
      showGeneralMessage("Process failed.", false);
    } finally {
      setLoading(false);
    }
  }, [downloadPDF, showGeneralMessage]);

  return { loading, generateAndDownload, showMessage, message, isSuccess };
};